import React, { useState, useEffect } from 'react';
import { authFetch } from '@/utils/auth-fetch';
import { sitePermissionsService } from '@/services/site-permissions';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Shield,
  UserPlus,
  Trash2,
  Check,
  X,
  Building2,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  approved: boolean;
  admin_approved: boolean;
  email_verified: boolean;
  created_at: string;
}

interface Site {
  id: string;
  name: string;
  url: string;
}

interface SitePermission {
  id: string;
  user_id: string;
  site_id: string;
  granted_at: string;
  site_name?: string;
  user_name?: string;
  user_email?: string;
}

export default function SitePermissions() {
  const [users, setUsers] = useState<User[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userPermissions, setUserPermissions] = useState<SitePermission[]>([]);
  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false);
  const [isPermissionDialogOpen, setIsPermissionDialogOpen] = useState(false);
  const [newRole, setNewRole] = useState<string>('');
  const [newSiteId, setNewSiteId] = useState<string>('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchSites();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await authFetch('/api/users');

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      setUsers(data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchSites = async () => {
    try {
      const response = await authFetch('/api/sites');

      if (!response.ok) {
        throw new Error('Failed to fetch sites');
      }

      const data = await response.json();
      setSites(data || []);
    } catch (error) {
      console.error('Error fetching sites:', error);
      toast.error('Failed to load sites');
    }
  };

  const fetchUserPermissions = async (userId: string) => {
    try {
      const permissions = await sitePermissionsService.getUserSites(userId);
      setUserPermissions(permissions);
    } catch (error) {
      console.error('Error fetching user permissions:', error);
      toast.error('Failed to load user permissions');
    }
  };

  const handleSelectUser = async (user: User) => {
    setSelectedUser(user);
    await fetchUserPermissions(user.id);
  };

  const handleUpdateRole = async () => {
    if (!selectedUser || !newRole) return;

    try {
      setSaving(true);
      const response = await authFetch(`/api/users/${selectedUser.id}/role`, {
        method: 'PUT',
        body: JSON.stringify({ role: newRole }),
      });

      if (!response.ok) {
        throw new Error('Failed to update role');
      }

      toast.success(`User role updated to ${newRole}`);
      setIsRoleDialogOpen(false);
      await fetchUsers();

      // Refresh selected user data
      const updatedUser = users.find(u => u.id === selectedUser.id);
      if (updatedUser) {
        setSelectedUser({ ...updatedUser, role: newRole });
      }
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error('Failed to update user role');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleApproval = async () => {
    if (!selectedUser) return;

    try {
      setSaving(true);
      const newApprovalStatus = !selectedUser.admin_approved;
      const response = await authFetch(`/api/users/${selectedUser.id}/approve`, {
        method: 'PUT',
        body: JSON.stringify({ approved: newApprovalStatus }),
      });

      if (!response.ok) {
        throw new Error('Failed to update approval status');
      }

      toast.success(newApprovalStatus ? 'User approved - can now log in' : 'User approval revoked');
      await fetchUsers();

      // Update selected user
      setSelectedUser({
        ...selectedUser,
        approved: newApprovalStatus,
        admin_approved: newApprovalStatus,
        email_verified: newApprovalStatus,
      });
    } catch (error) {
      console.error('Error toggling approval:', error);
      toast.error('Failed to update user approval');
    } finally {
      setSaving(false);
    }
  };

  const handleGrantPermission = async () => {
    if (!selectedUser || !newSiteId) return;

    try {
      setSaving(true);
      await sitePermissionsService.grantPermission(selectedUser.id, newSiteId);

      toast.success('Site permission granted successfully');
      setIsPermissionDialogOpen(false);
      setNewSiteId('');
      await fetchUserPermissions(selectedUser.id);
    } catch (error) {
      console.error('Error granting permission:', error);
      toast.error('Failed to grant site permission');
    } finally {
      setSaving(false);
    }
  };

  const handleRevokePermission = async (permission: SitePermission) => {
    if (!selectedUser) return;

    try {
      await sitePermissionsService.revokePermission(
        permission.user_id,
        permission.site_id
      );

      toast.success('Site permission revoked successfully');
      await fetchUserPermissions(selectedUser.id);
    } catch (error) {
      console.error('Error revoking permission:', error);
      toast.error('Failed to revoke site permission');
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'site_content_manager':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'Super Admin';
      case 'site_content_manager':
        return 'Site Content Manager';
      default:
        return role;
    }
  };

  const availableSites = sites.filter(
    (site) => !userPermissions.some((p) => p.site_id === site.id)
  );

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">Loading...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Shield className="h-8 w-8" />
            Site Permissions Management
          </h1>
          <p className="text-muted-foreground mt-2">
            Manage user roles and site access permissions for content managers
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Users List */}
        <Card>
          <CardHeader>
            <CardTitle>Users</CardTitle>
            <CardDescription>
              Select a user to view and manage their permissions
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow
                    key={user.id}
                    className={
                      selectedUser?.id === user.id
                        ? 'bg-muted'
                        : 'cursor-pointer hover:bg-muted/50'
                    }
                    onClick={() => handleSelectUser(user)}
                  >
                    <TableCell className="font-medium">{user.name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Badge className={getRoleBadgeColor(user.role)}>
                        {getRoleLabel(user.role)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {user.admin_approved ? (
                        <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                          Approved
                        </Badge>
                      ) : (
                        <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                          Pending
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {selectedUser?.id === user.id && (
                        <Check className="h-4 w-4 text-green-600" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* User Permissions */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>
                  {selectedUser
                    ? `Permissions for ${selectedUser.name}`
                    : 'Select a User'}
                </CardTitle>
                <CardDescription>
                  {selectedUser
                    ? `Manage role and site access for ${selectedUser.email}`
                    : 'Choose a user from the list to view their permissions'}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {!selectedUser ? (
              <div className="text-center py-12 text-muted-foreground">
                <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-20" />
                <p>Select a user to manage their permissions</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Login Approval */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="font-medium">Login Approval</h3>
                      <p className="text-sm text-muted-foreground">
                        {selectedUser.admin_approved
                          ? '✓ User is approved and can log in'
                          : '⚠ User is pending approval and cannot log in'}
                      </p>
                    </div>
                    <Button
                      variant={selectedUser.admin_approved ? 'outline' : 'default'}
                      size="sm"
                      onClick={handleToggleApproval}
                      disabled={saving}
                    >
                      {selectedUser.admin_approved ? 'Revoke Access' : 'Approve User'}
                    </Button>
                  </div>
                </div>

                {/* Role Management */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="font-medium">User Role</h3>
                      <p className="text-sm text-muted-foreground">
                        Current role: {getRoleLabel(selectedUser.role)}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setNewRole(selectedUser.role);
                        setIsRoleDialogOpen(true);
                      }}
                    >
                      Change Role
                    </Button>
                  </div>
                </div>

                {/* Site Permissions */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-medium">Site Permissions</h3>
                      <p className="text-sm text-muted-foreground">
                        {selectedUser.role === 'super_admin'
                          ? 'Super admins have access to all sites'
                          : `User has access to ${userPermissions.length} site(s)`}
                      </p>
                    </div>
                    {selectedUser.role === 'site_content_manager' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsPermissionDialogOpen(true)}
                        disabled={availableSites.length === 0}
                      >
                        <UserPlus className="h-4 w-4 mr-2" />
                        Grant Access
                      </Button>
                    )}
                  </div>

                  {selectedUser.role === 'super_admin' ? (
                    <div className="bg-purple-50 dark:bg-purple-950/20 rounded-lg p-4 text-center">
                      <Shield className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                      <p className="text-sm text-purple-800 dark:text-purple-200">
                        Super admins have universal access to all sites
                      </p>
                    </div>
                  ) : userPermissions.length === 0 ? (
                    <div className="bg-muted rounded-lg p-4 text-center">
                      <Building2 className="h-8 w-8 text-muted-foreground mx-auto mb-2 opacity-20" />
                      <p className="text-sm text-muted-foreground">
                        No site permissions granted yet
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {userPermissions.map((permission) => (
                        <div
                          key={permission.id}
                          className="flex items-center justify-between p-2 rounded-lg border bg-card"
                        >
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm font-medium">
                              {permission.site_name || 'Unknown Site'}
                            </span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRevokePermission(permission)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Change Role Dialog */}
      <Dialog open={isRoleDialogOpen} onOpenChange={setIsRoleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change User Role</DialogTitle>
            <DialogDescription>
              Update the role for {selectedUser?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={newRole} onValueChange={setNewRole}>
              <SelectTrigger>
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="user">Regular User</SelectItem>
                <SelectItem value="site_content_manager">
                  Site Content Manager
                </SelectItem>
                <SelectItem value="super_admin">Super Admin</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground mt-2">
              {newRole === 'super_admin' &&
                'Super admins have full access to all features and sites.'}
              {newRole === 'site_content_manager' &&
                'Content managers can manage content for assigned sites.'}
              {newRole === 'user' && 'Regular users have limited access.'}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRoleDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateRole} disabled={saving || !newRole}>
              {saving ? 'Updating...' : 'Update Role'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Grant Site Permission Dialog */}
      <Dialog
        open={isPermissionDialogOpen}
        onOpenChange={setIsPermissionDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Grant Site Permission</DialogTitle>
            <DialogDescription>
              Grant {selectedUser?.name} access to a site
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={newSiteId} onValueChange={setNewSiteId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a site" />
              </SelectTrigger>
              <SelectContent>
                {availableSites.map((site) => (
                  <SelectItem key={site.id} value={site.id}>
                    {site.name} ({site.url})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {availableSites.length === 0 && (
              <p className="text-sm text-muted-foreground mt-2">
                User already has access to all sites
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsPermissionDialogOpen(false);
                setNewSiteId('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleGrantPermission}
              disabled={saving || !newSiteId}
            >
              {saving ? 'Granting...' : 'Grant Access'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
